import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Truck, ShieldCheck, MessageCircle, RefreshCw, ArrowRight, Star } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1708534419572-6e6614a53ca1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CATS = [
  { name: "Kurtis & Tunics", img: "https://images.pexels.com/photos/13178920/pexels-photo-13178920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=500" },
  { name: "Suit Sets", img: "https://images.unsplash.com/photo-1764740185240-58527413f572?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
  { name: "Anarkalis", img: "https://images.pexels.com/photos/8770996/pexels-photo-8770996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=500" },
  { name: "Fusion Dresses", img: "https://images.unsplash.com/photo-1646979201169-e83d743bfa8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/products", { params: { skus: undefined } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const bestsellers = [...products].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 4);
  const newArrivals = products.filter((p) => p.is_new).slice(0, 4);
  const featured = (settings?.featured_skus || []).map((sku) => products.find((p) => p.sku === sku)).filter(Boolean);
  const DEFAULT_SLIDE = { image: HERO, eyebrow: "Festive Edit · 2026", title: "Drape Yourself in Royal Craftsmanship", subtitle: "Handwoven kurtis, regal anarkalis & contemporary suit sets.", cta: "Shop The Collection", link: "/shop" };
  const userSlides = (settings?.hero_slides || []).filter((s) => s.image);
  const slides = settings ? (userSlides.length ? userSlides : [DEFAULT_SLIDE]) : [];
  const [activeSlide, setActiveSlide] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setActiveSlide((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);
  const slide = slides[activeSlide % slides.length] || DEFAULT_SLIDE;

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[78vh]">
          <div className="lg:col-span-5 flex items-center bg-[#fdfbf7] px-6 sm:px-12 lg:px-16 py-16 order-2 lg:order-1">
            <div className="fade-up" key={activeSlide}>
              <p className="accent-label">{slide.eyebrow}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-rose-950 mt-4">
                {slide.title}
              </h1>
              <p className="text-muted-foreground mt-5 leading-relaxed max-w-md">
                {slide.subtitle}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to={slide.link || "/shop"}><Button className="bg-rose-950 hover:bg-rose-900 h-12 px-8 text-base rounded-full" data-testid="hero-shop-btn">{slide.cta || "Shop The Collection"} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
                <Link to="/shop?category=Anarkalis"><Button variant="outline" className="h-12 px-8 text-base rounded-full border-rose-950/30 text-rose-950">Festive Anarkalis</Button></Link>
              </div>
              {slides.length > 1 && (
                <div className="flex gap-2 mt-8" data-testid="hero-dots">
                  {slides.map((_, i) => (
                    <button key={i} onClick={() => setActiveSlide(i)} aria-label={`Slide ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeSlide ? "w-8 bg-rose-950" : "w-3 bg-rose-950/25 hover:bg-rose-950/50"}`} data-testid={`hero-dot-${i}`} />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-6 mt-10">
                <div><p className="font-serif text-2xl font-bold text-rose-950">50k+</p><p className="text-xs text-muted-foreground">Happy Women</p></div>
                <div className="w-px h-10 bg-rose-900/15" />
                <div><p className="font-serif text-2xl font-bold text-rose-950">4.8★</p><p className="text-xs text-muted-foreground">Avg Rating</p></div>
                <div className="w-px h-10 bg-rose-900/15" />
                <div><p className="font-serif text-2xl font-bold text-rose-950">3-Day</p><p className="text-xs text-muted-foreground">Easy Returns</p></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative order-1 lg:order-2 min-h-[42vh] lg:min-h-full bg-rose-950/5">
            {slides.map((s, i) => (
              <img key={i} src={s.image || HERO} alt={s.title || "Royal ethnic wear"} className={`img-cover absolute inset-0 transition-opacity duration-700 ${i === activeSlide ? "opacity-100" : "opacity-0"}`} data-testid={`hero-slide-img-${i}`} />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-rose-950/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-rose-950 text-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6 py-8">
          {[[MessageCircle, "Instant WhatsApp Updates"], [Truck, "Shiprocket Pan-India Delivery"], [RefreshCw, "3-Day Easy Returns"], [ShieldCheck, "100% Secure Prepaid Payments"]].map(([Icon, t], i) => (
            <div key={i} className="flex items-center gap-3 justify-center lg:justify-start">
              <Icon className="w-6 h-6 text-amber-500 shrink-0" />
              <span className="text-sm font-accent">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED SLIDER */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16">
          <div className="flex items-end justify-between mb-8">
            <div><p className="accent-label">Handpicked For You</p><h2 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2">Featured Collection</h2></div>
            <Link to="/shop" className="font-accent text-sm text-amber-600 flex items-center gap-1 hover:gap-2 transition-all">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="flex gap-4 lg:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory rw-hscroll" data-testid="home-featured-slider">
            {featured.map((p, i) => (
              <div key={p.id} className="snap-start shrink-0 w-[46%] sm:w-[31%] lg:w-[23.5%]">
                <ProductCard p={p} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
        <div className="text-center mb-10">
          <p className="accent-label">Curated Collections</p>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2">Shop By Category</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {CATS.map((c, i) => (
            <Link key={c.name} to={`/shop?category=${encodeURIComponent(c.name)}`} className="group relative rounded-xl overflow-hidden aspect-[3/4] fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`category-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <img src={c.img} alt={c.name} className="img-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-rose-950/80 via-rose-950/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-lg lg:text-xl font-semibold text-amber-50">{c.name}</h3>
                <span className="text-xs text-amber-200 font-accent flex items-center gap-1 mt-1">Explore <ArrowRight className="w-3 h-3" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16">
        <div className="flex items-end justify-between mb-8">
          <div><p className="accent-label">Most Loved</p><h2 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2">Bestsellers</h2></div>
          <Link to="/shop" className="font-accent text-sm text-amber-600 flex items-center gap-1 hover:gap-2 transition-all">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {bestsellers.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16">
        <div className="relative rounded-2xl overflow-hidden bg-rose-950 px-8 lg:px-16 py-14 lg:py-20">
          <div className="relative z-10 max-w-lg">
            <p className="accent-label !text-amber-400">WhatsApp Shopping</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-amber-50 mt-3">Order & Track Right From WhatsApp</h2>
            <p className="text-amber-50/70 mt-4 leading-relaxed">Login with just your mobile number. Get order confirmation, shipping label, tax invoice & live tracking — all delivered instantly to your WhatsApp.</p>
            <Link to="/login"><Button className="mt-7 bg-amber-600 hover:bg-amber-700 h-12 px-8 rounded-full text-base" data-testid="banner-whatsapp-btn">Login with WhatsApp</Button></Link>
          </div>
          <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20 lg:opacity-40">
            <MessageCircle className="w-full h-full text-amber-500" strokeWidth={0.5} />
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div><p className="accent-label">Fresh Off The Loom</p><h2 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2">New Arrivals</h2></div>
          <Link to="/shop?sort=new" className="font-accent text-sm text-amber-600 flex items-center gap-1 hover:gap-2 transition-all">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {newArrivals.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-accent/40 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-10"><p className="accent-label">Loved By Thousands</p><h2 className="font-serif text-3xl font-bold text-rose-950 mt-2">What Our Women Say</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[["Priya S.", "The anarkali quality is stunning! Got WhatsApp updates at every step. Delivery was super fast."], ["Ananya R.", "Fabric is premium and true to colour. Easy return process, loved the experience."], ["Meera K.", "Ordered for Diwali, reached in 3 days via Shiprocket. The embroidery is gorgeous!"]].map(([n, t], i) => (
              <div key={i} className="bg-white rounded-xl p-6 hover-lift">
                <div className="flex gap-0.5 text-amber-500 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-500" />)}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">"{t}"</p>
                <p className="font-serif font-semibold text-rose-950 mt-4">{n}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
